import type { AdminBlogArticle, BlogArticle } from '~/utils/interfaces'

// Helpers for the (thumbnail-free) blog: text excerpts, reading time, and canonical paths.

// Strip Markdown/HTML to plain text for excerpts.
function stripMarkup(input: string): string {
    if (!input) return '';
    return input
        .replace(/```[\s\S]*?```/g, ' ')        // fenced code blocks
        .replace(/`[^`]*`/g, ' ')               // inline code
        .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')  // images
        .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // links -> text
        .replace(/<[^>]+>/g, ' ')               // html tags
        .replace(/^[#>\-*\s]+/gm, ' ')          // heading/list/quote markers
        .replace(/[*_~]/g, '')                  // emphasis markers
        .replace(/\s+/g, ' ')
        .trim();
}

// Prefer the article's description; fall back to the first ~40 words of the body.
export function blogExcerpt(description: string | undefined | null, body: string, maxWords = 40): string {
    const desc = stripMarkup(description || '');
    if (desc) return desc;
    const words = stripMarkup(body || '').split(' ').filter(Boolean);
    if (words.length <= maxWords) return words.join(' ');
    return words.slice(0, maxWords).join(' ') + '…';
}

// Estimate reading time in minutes (~200 words/min, minimum 1).
export function blogReadingTime(body: string): number {
    const words = stripMarkup(body || '').split(' ').filter(Boolean).length;
    return Math.max(1, Math.round(words / 200));
}

export function blogArticlePath(article: Pick<BlogArticle, 'category' | 'slug'>): string {
    return `/blog/${article.category.slug}/${article.slug}`
}

// Drafts use the real public article route, but opt into the authenticated admin payload.
// Published posts keep their canonical URL so previews behave exactly like normal visitors see them.
export function blogPreviewPath(article: AdminBlogArticle): string {
    const path = blogArticlePath(article)
    return article.published ? path : `${path}?preview=${article.id}`
}
